import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthService, LoginMethod } from 'src/app/services/Auth/auth.service';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { APIService, OktaDatabaseOption, OktaLoginRequest } from 'src/app/services/APIService/api.service';
import { MsalConfigService } from 'src/app/services/msal-config.service';
import { IsRep } from 'src/app/services/_offline/dexieDB';
import { ClarityModule } from '@clr/angular';
import { FormsModule } from '@angular/forms';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [ClarityModule, FormsModule, MrTranslatePipe],
})
export class HomeComponent implements OnInit {
  oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private apiService = inject(APIService);
  private msalConfigService = inject(MsalConfigService);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private toastr = inject(ToastService);
  private router = inject(Router);

  userName?: string;
  error?: Error;

  /** Datenbankauswahl, wenn die Okta-Gruppen auf mehrere Anwendungsbereiche zeigen (z. B. FAT und Messe-Demo). */
  protected isDatabaseSelectVisible = false;
  protected databaseChoices: OktaDatabaseOption[] = [];
  protected selectedDatabase = '';
  private pendingOktaLoginRequest?: OktaLoginRequest;

  async ngOnInit(): Promise<void> {
    try {
      const oktaSuccess = await this.tryLoginWithOkta();
      if (oktaSuccess) {
        return;
      }

      const msSuccess = await this.tryLoginWithMicrosoft();
      if (msSuccess) {
        return;
      }

      if (IsRep()) {
        this.router.navigateByUrl('/anlagen');
        return;
      }

      this.router.navigateByUrl('/login');
    } catch (err) {
      console.error('🔧 [HOME] Authentication error:', err);
      this.handleError(err);
      this.router.navigateByUrl('/login');
    }
  }

  async tryLoginWithOkta(): Promise<boolean> {
    let isLoggedInToOkta = await this.oktaAuth.isAuthenticated();

    if (!isLoggedInToOkta && window.location.search.includes('iss=')) {
      isLoggedInToOkta = await this.tryLoginOktaSSO();
    }

    if (isLoggedInToOkta) {
      try {
        await this.loginToApiUsingOkta();
        return true;
      } catch (error) {
        console.error('🔧 [OKTA] Error logging into API using Okta:', error);
        this.handleOktaApiError(error);
        return false;
      }
    }

    return false;
  }

  async tryLoginOktaSSO(): Promise<boolean> {
    try {
      const tokenResponse = await this.oktaAuth.token.getWithoutPrompt({
        scopes: ['openid', 'profile', 'email', 'groups-mrpro'],
        responseMode: 'okta_post_message',
      });
      this.oktaAuth.tokenManager.setTokens(tokenResponse.tokens);
      window.history.replaceState({}, document.title, window.location.pathname);

      const isAuth = await this.oktaAuth.isAuthenticated();
      return isAuth;
    } catch (error) {
      await this.oktaAuth.signInWithRedirect({
        scopes: ['openid', 'profile', 'email', 'groups-mrpro'],
      });
      return false;
    }
  }

  async loginToApiUsingOkta(): Promise<void> {
    try {
      const userClaims = await this.oktaAuth.getUser();
      const idToken = this.oktaAuth.getIdToken();
      const decodedToken = this.oktaAuth.token.decode(idToken) as any;
      const groupsTheUserIsIn = decodedToken.payload['groups-mrpro'] || [];

      this.userName = userClaims.name;

      // Extract given_name and family_name with fallbacks
      let givenName = userClaims.given_name;
      let familyName = userClaims.family_name;

      // If claims don't have names, try to extract from email or name
      if (!givenName || !familyName) {
        if (userClaims.name) {
          const nameParts = userClaims.name.split(' ');
          givenName = givenName || nameParts[0] || 'Unknown';
          familyName = familyName || nameParts.slice(1).join(' ') || 'User';
        } else if (userClaims.email) {
          // Last resort: use email prefix
          const emailPrefix = userClaims.email.split('@')[0];
          givenName = givenName || emailPrefix;
          familyName = familyName || 'User';
        }
      }

      const oktaLoginRequest: OktaLoginRequest = {
        email: userClaims.email,
        username: this.userName || userClaims.email,
        givenName: givenName,
        familyName: familyName,
        usergruppen: groupsTheUserIsIn,
      };

      await this.requestApiTokenForOkta(oktaLoginRequest);
    } catch (error) {
      console.error('🔧 [API-OKTA] Error in loginToApiUsingOkta:', error);
      throw error; // Re-throw to be caught by the calling function
    }
  }

  /**
   * Holt das MR.pro-Token zu einer Okta-Sitzung. Zeigen die Okta-Gruppen des Benutzers auf mehrere
   * Datenbanken (z. B. FAT und Messe-Demo), liefert das Backend statt eines Tokens eine Auswahlliste.
   */
  private async requestApiTokenForOkta(oktaLoginRequest: OktaLoginRequest): Promise<void> {
    const tokenResponse = await firstValueFrom(this.authService.getOktaMRproToken(oktaLoginRequest));
    if (!tokenResponse) {
      console.error('🔧 [API-OKTA] Failed to get token response');
      throw new Error('Logged in to Okta but could not retrieve the API token');
    }

    if (tokenResponse.databaseSelectionRequired) {
      this.pendingOktaLoginRequest = oktaLoginRequest;
      this.databaseChoices = tokenResponse.databases ?? [];
      this.selectedDatabase = this.databaseChoices[0]?.name ?? '';
      this.isDatabaseSelectVisible = true;
      return;
    }

    sessionStorage.removeItem('okta-jwt-tmp');

    this.authService.loginMethod = LoginMethod.Okta;

    this.authService.handleLogin(tokenResponse);

    // Wait for the authentication state to be fully established
    await firstValueFrom(
      this.authService.authState$.pipe(
        filter(authState => authState.isAuthenticated && authState.permissionsLoaded),
        first()
      )
    );

    // Wait an additional moment for the authService.isLoggedIn() to be true
    // This ensures the AuthGuard will pass
    await new Promise(resolve => setTimeout(resolve, 500));

    // Verify auth state before navigation
    const isLoggedIn = this.authService.isLoggedIn();

    if (!isLoggedIn) {
      console.error('🔧 [API-OKTA] User is not logged in according to authService.isLoggedIn()');
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    // Let the auth service handle navigation to avoid double redirects
    // The handleLogin method will call navigateAfterLogin() which handles the appropriate route
  }

  /** Wiederholt die Okta-Anmeldung mit der vom Benutzer gewählten Datenbank. */
  protected async confirmDatabaseSelection(): Promise<void> {
    if (!this.selectedDatabase || !this.pendingOktaLoginRequest) return;

    const request: OktaLoginRequest = { ...this.pendingOktaLoginRequest, database: this.selectedDatabase };
    this.isDatabaseSelectVisible = false;
    this.pendingOktaLoginRequest = undefined;

    try {
      await this.requestApiTokenForOkta(request);
    } catch (error) {
      console.error('🔧 [API-OKTA] Error completing Okta login with selected database:', error);
      this.handleOktaApiError(error);
    }
  }

  /** Bricht die Datenbankauswahl ab und kehrt zur Anmeldemaske zurück. */
  protected cancelDatabaseSelection(): void {
    this.isDatabaseSelectVisible = false;
    this.databaseChoices = [];
    this.selectedDatabase = '';
    this.pendingOktaLoginRequest = undefined;
    this.router.navigateByUrl('/login');
  }
  async tryLoginWithMicrosoft(): Promise<boolean> {
    console.log('[MSAL] Checking for existing MSAL session...');
    if (await this.msalConfigService.isInitialized()) {
      const instance = await this.msalConfigService.getInstance();
      if (!instance) {
        console.warn('[MSAL] MSAL instance is null, skipping silent login');
        return false;
      }

      try {
        console.log('[MSAL] Attempting silent token acquisition...');
        const token = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
        });
        const account = instance.getAllAccounts()?.[0];
        console.log('[MSAL] Silent token acquired', {
          email: account?.username,
          tenantId: account?.tenantId,
          hasIdToken: !!token.idToken,
        });

        // Use idToken instead of accessToken — with OIDC-only scopes there is no resource access token
        await this.loginToApiUsingMSAL(account.username, token.idToken);
        return true;
      } catch (silentError) {
        console.warn('[MSAL] Silent token acquisition failed:', silentError);
        return false;
      }
    }

    console.log('[MSAL] No MSAL session found');
    return false;
  }

  async loginToApiUsingMSAL(email: string, idToken: string): Promise<void> {
    console.log('[MSAL] Sending idToken to backend for API login', { email });
    const result = await this.authService.msalLogin(email, idToken).toPromise();
    console.log('[MSAL] Backend API login response:', result);

    if (result.error !== undefined) {
      console.error('[MSAL] Backend returned error:', result.error, result.info || '');
      this.toastr.error(this.translateService.instant('Login fehlgeschlagen'));
      this.router.navigateByUrl('/login');
      return;
    }

    console.log('[MSAL] API login successful');
    this.authService.loginMethod = LoginMethod.Microsoft;
    localStorage.setItem('loginMethod', 'Microsoft');

    // Let the auth service handle navigation after successful login
    // This prevents double redirects and navigation conflicts
  }

  private handleError(error: Error) {
    this.error = error;
    const errorMessage = this.translateService.instant('An error occurred');
    this.toastr.error(errorMessage);
    this.sendErrorToLog(error);
  }

  private handleOktaApiError(error: any) {
    console.error('[OKTA-ERROR] Handling Okta API error:', error);

    let errorMessage = this.translateService.instant('Okta authentication failed');

    // Check if this is an HTTP error response
    if (error?.error && typeof error.error === 'object') {
      const backendError = error.error;

      // Handle specific backend error cases
      if (backendError.databaseError) {
        errorMessage = this.translateService.instant('Database configuration error. Please contact support.');
      } else if (backendError.userType === 'external' && backendError.error?.includes('No database configuration')) {
        errorMessage = this.translateService.instant(
          'No database access configured for your account. Please contact support.'
        );
      } else if (backendError.userType === 'internal' && backendError.error?.includes('not found')) {
        errorMessage = this.translateService.instant('Internal user account not found. Please contact support.');
      } else if (backendError.error?.includes('Database connection')) {
        errorMessage = this.translateService.instant('Database connection error. Please try again later.');
      } else if (backendError.error && typeof backendError.error === 'string') {
        // Use the backend error message
        errorMessage = backendError.error;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }

    this.toastr.error(errorMessage, this.translateService.instant('Authentication Error'), {
      timeOut: 10000, // Show for 10 seconds
      extendedTimeOut: 5000,
    });

    // Navigate back to login page
    this.router.navigateByUrl('/login');

    // Also send error to log
    this.sendErrorToLog(error);
  }

  private sendErrorToLog(error: Error): void {
    const errorPayload = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    };

    // Send error payload to the backend via POST.
    this.apiService.logError(errorPayload).subscribe({
      error: err => console.error('[LOG-ERROR] Failed to log error', err),
    });
  }
}
